import axios from 'axios';
import moment from 'moment'
import _ from 'lodash'
import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import {
  Timesheet,
  TimesheetsState,
  Schedule,
  WorkingTime,
  Other,
  WorkingDay,
  PaidAbsentDay,
  UnpaidAbsentDay,
  TimesheetConditions,
  DownloadLessonSchedulesCsvConditions,
  DownloadOtherSchedulesCsvConditions,
  TimesheetCloseParams,
  WorkingResultConditions,
  WorkSummaryDiff,
  WorkSummary,
  yearlyworkdays,
} from '@/store/timesheets/types';
import { Sort } from '@/store/sort.types';
import { getPagerFromApiResponse } from '@/store/utils'

const actions: ActionTree<TimesheetsState, RootState> = {
  /**
   * タイムシート一覧
   * @param { number } page ページ
   * @param { TimesheetConditions } 検索条件
   */
  fetchTimesheets: (
    { commit },
    { page, conditions, sortBy } : { conditions?: TimesheetConditions, page: number, sortBy: Sort },
  ) => axios.get('/tutors/timeSheet', {
    params: {
      ..._.pickBy({
        yearMonth: conditions?.yearMonth.format('YYYYMM'),
        positionCode: conditions?.positions?.map(({ code }) => code).join(','),
        tutor: conditions?.tutorNameOrId,
        isConfirmed: conditions?.confirmed?.map((c) => _.toNumber(c)).join(','),
        sortBy: _.values(sortBy).join(':'),
      }),
      page,
    },
  })
    .then(({ headers, data }) => {
      const timesheets: Timesheet[] = data.map((timesheet: any): Timesheet => {
        return {
          tutor: {
            id: timesheet.tutorId,
            name: timesheet.tutorName,
            position: {
              name: timesheet.position,
              code: timesheet.positionCode,
            },
          },
          date: moment(timesheet.yearMonth),
          isConfirmed: timesheet.isConfirmed,
        }
      })
      commit('fetchedTimesheet', {
        timesheets,
        conditions,
        pager: getPagerFromApiResponse(page, headers),
        sortBy,
      });
    }),

  /*
  * タイムシート詳細
  */
  getTimesheet: (
    {
      commit,
      dispatch,
      rootState,
      getters,
    },
    { tutorId, yearMonth }: { tutorId: String, yearMonth: Number },
  ) => {
    return Promise.all([
      axios.get(`/tutors/${tutorId}/timeSheet`, {
        params: {
          yearMonth,
        },
      }),
      dispatch('getTutor', { tutorId }),
      dispatch('fetchMasterLevels'),
      dispatch('fetchMasterUniversities'),
      dispatch('fetchMasterCampuses'),
    ])
      .then(([{ data }]) => {
        const { levels, universities, campuses } = rootState.master

        const tutor = getters.findTutorById(data.tutorId)
        if (_.isUndefined(tutor)) {
          throw Error(`Cannot found tutor - tutorId: ${data.tutorId}`)
        }

        const { isConfirmed } = data

        const schedules: Schedule[] = _.chain(data.timeSheets)
          .map(({ day, schedules: ss }: any) => _.map(ss, (schedule): Schedule => {
            const level = _.find(levels, { code: schedule.levelCode }) || null
            if (schedule.levelCode && _.isUndefined(level)) {
              throw Error(`Cannot found level - code: ${schedule.levelCode}`)
            }

            const university = _.find(universities, {
              abbreviation: schedule.universityAbbreviation,
            }) || null
            const campus = _.find(campuses, {
              abbreviation: schedule.campusAbbreviation,
            }) || null

            let scheduleParticipantTime
            if (schedule.type === 'lesson' || schedule.type === 'subLesson' || schedule.type === 'waiting') {
              scheduleParticipantTime = null
            } else {
              scheduleParticipantTime = schedule.scheduleParticipantTime
            }

            return {
              date: moment(`${yearMonth}${day}`, 'YYYYMMDD').startOf('date'),
              start: schedule.startPeriod,
              end: schedule.endPeriod,
              type: schedule.type,
              id: schedule.scheduleId,
              substituteTime: schedule.substituteTime,
              participantTime: scheduleParticipantTime,
              level,
              university,
              campus,
            }
          }))
          .flatten()
          .value()

        const summary: {
          workingTimes: WorkingTime[],
          others: Other[],
          workingDays: WorkingDay[],
          paidAbsentDay: PaidAbsentDay,
          unpaidAbsentDays: UnpaidAbsentDay[],
        } = {
          workingTimes: [
            ..._.map(data.workingTime?.campuses, (wt): WorkingTime => ({
              category: 'campus',
              term: 'month',
              unit: 'lesson',
              university: _.find(universities, { name: wt.universityName }) || null,
              campus: _.find(campuses, { name: wt.campusName }) || null,
              absent: wt.absentCount,
              late: wt.lateCount,
              lesson: wt.lessonCount,
              substitute: wt.substituteCount,
              waiting: wt.waitingCount,
              workOnHoliday: wt.workOnHolidayCount,
            })),
            {
              category: 'total',
              term: 'month',
              unit: 'lesson',
              university: null,
              campus: null,
              absent: data.workingTime?.totalCount?.absentCount,
              late: data.workingTime?.totalCount?.lateCount,
              lesson: data.workingTime?.totalCount?.lessonCount,
              substitute: data.workingTime?.totalCount?.substituteCount,
              waiting: data.workingTime?.totalCount?.waitingCount,
              workOnHoliday: data.workingTime?.totalCount?.workOnHolidayCount,
            },
            {
              category: 'total',
              term: 'month',
              unit: 'hour',
              university: null,
              campus: null,
              absent: data.workingTime?.totalTime?.absentTime,
              late: data.workingTime?.totalTime?.lateTime,
              lesson: data.workingTime?.totalTime?.lessonTime,
              substitute: data.workingTime?.totalTime?.substituteTime,
              waiting: data.workingTime?.totalTime?.waitingTime,
              workOnHoliday: null,
            },
          ],
          others: [{
            unit: 'hour',
            term: 'month',
            meeting: data.other.meetingTime,
            workshop: data.other.workshopTime,
          }],
          workingDays: _.map(data.workingDay, (wd, key): WorkingDay => {
            // if ((key !== 'month') && (key !== 'fiscalYear')) {
            //   throw Error(`タイムシート詳細にterm定義以外のtermが定義されました: ${key}`)
            // }
            if ((key !== 'month')) {
              throw Error(`タイムシート詳細にterm定義以外のtermが定義されました: ${key}`)
            }
            return {
              term: key,
              unit: 'day',
              absent: wd.absentCount,
              attendance: wd.attendanceCount,
              late: wd.lateCount,
            }
          }),
          paidAbsentDay: {
            unit: 'day',
            term: 'month',
            nowLeft: data.paidAbsentDay.nowLeftCount,
            request: data.paidAbsentDay.requestThisMonthCount,
          },
          unpaidAbsentDays: [{
            unit: 'day',
            term: 'month',
            unpaidLeave: data.unpaidAbsentDay.month.unpaidLeaveCount,
            childCareLeave: data.unpaidAbsentDay.month.childCareLeaveCount,
            familyCare: data.unpaidAbsentDay.month.familyCareLeaveCount,
            flexibleWorkLeaveForChildCare: data.unpaidAbsentDay.month.flexibleWorkLeaveForChildCareCount,
            other: data.unpaidAbsentDay.month.otherCount,
          }, {
            unit: 'day',
            term: 'fiscalYear',
            unpaidLeave: data.unpaidAbsentDay.fiscalYear.unpaidLeaveCount,
            childCareLeave: data.unpaidAbsentDay.fiscalYear.childCareLeaveCount,
            familyCare: data.unpaidAbsentDay.fiscalYear.familyCareLeaveCount,
            flexibleWorkLeaveForChildCare: data.unpaidAbsentDay.fiscalYear.flexibleWorkLeaveForChildCareCount,
            other: data.unpaidAbsentDay.fiscalYear.otherCount,
          }],
        }

        const timeSheet: Timesheet = {
          tutor,
          date: moment(String(yearMonth), 'YYYYMM').startOf('month'),
          schedules,
          summary,
          isConfirmed,
        }

        commit('gotTimesheet', timeSheet);
      });
  },

  /**
   * 出勤日
   */
  getYearlyWorkingDay: async (
    { commit },
    { tutorId, yearMonth } : { tutorId: string, yearMonth: Number },
  ) => {
    return axios.get(`/tutors/${tutorId}/timeSheet/yearlyworkdays`, {
      params: {
        yearMonth,
      },
    })
      .then((response) => {
        const yearlyDays: yearlyworkdays[] = _.map(response.data, (wd: any): yearlyworkdays => ({
          attendance: wd.attendanceCount,
          late: wd.lateCount,
          absent: wd.absentCount,
        }))
        commit('gotYearlyWorkingDay', yearlyDays)
      })
  },

  /**
   * 勤務集計：チューターのタイムシート月締め処理
   */
  timesheetClose: (
    {
      commit,
    },
    timesheetClose: TimesheetCloseParams,
  ) => axios.post('/tutors/timeSheet/close', [timesheetClose]),

  /**
   * 勤務集計：集計実施
   */
  workingResultsAggregate: (
    { commit },
    { conditions }: { conditions: WorkingResultConditions },
  ) => axios.post('/workingResults/aggregate', {
    yearMonth: conditions.yearMonth.format('YYYYMM'),
    tutorId: conditions.tutorIds.join(','),
  }),

  /**
   * 勤務集計：結果ダウンロード
   */
  downloadWorkingSummary: (
    context,
    { year, month }: { year: number, month: number },
  ) => axios.get('/workTotals/csv', {
    params: {
      year,
      month,
    },
  }),

  /**
   * 勤務集計一覧の差分を取得
   * @param {File} csv 勤務集計一覧のcsvファイル
   */
  confirmWorkSummaryDifference: async (
    { commit },
    { csv }: { csv: File },
  ) => {
    const formData = new FormData()
    formData.append('csv', csv)
    const { data } = await axios.post('/workTotals/diff', formData)

    const diffs = _.map(data, (diff: any) => {
      const { edit, current } = diff

      const converted = _.map([edit, current], (v): WorkSummary => ({
        tutorId: v.tutorId,
        tutorName: v.tutorName,
        yearMonth: v.yearMonth,
        position: v.position,
        attendance: v.attendance,
        paidAbsence: v.paidAbsence,
        transportationExpenses: v.transportationExpenses,
        sub: v.sub,
        waiting: v.waiting,
        extraLesson: v.extraLesson,
        comment: v.comment,
        marking: v.marking,
        canDoAssessment: v.canDoAssessment,
        number_10MinuteSub: v['10MinuteSub'],
        number_10MinuteDelay: v['10MinuteDelay'],
        meeting: v.meeting,
        tokorozawa: v.tokorozawa,
        sAbsence: v.sAbsence,
        dAbsence: v.dAbsence,
        wAbsence: v.wAbsence,
        bAbsence: v.bAbsence,
        lesson1_5: v['lesson1-5'],
        lesson6: v.lesson6,
        finalReview: v.finalReview,
        workshop: v.workshop,
        extraReviewSheets: v.extraReviewSheets,
        attendanceAllowance: v.attendanceAllowance,
        performanceAllowance: v.performanceAllowance,
        workshopOrientation: v.workshopOrientation,
        extraWork1: v.extraWork1,
        extraWork2: v.extraWork2,
        extraWork3: v.extraWork3,
        extraWork4: v.extraWork4,
        extraWork5: v.extraWork5,
      }))

      return {
        edit: _.first(converted),
        current: _.last(converted),
      }
    })

    commit('gotWorkSummaryDiff', diffs)
  },

  /**
   * 勤務集計一覧の一括更新
   * @param {File} csv 勤務集計一覧のcsvファイル
   */
  importWorkSummaryByCsv: async (
    { commit },
    { csv }: { csv: File },
  ) => {
    const formData = new FormData()
    formData.append('csv', csv)
    await axios.post('/workTotals/bulkUpdate', formData)
  },

  /**
   * レッスンスケジュールのCSVダウンロード
   * @param {DownloadLessonSchedulesCsvConditions} conditions 条件
   */
  downloadLessonsCsv: (
    context,
    conditions: DownloadLessonSchedulesCsvConditions,
  ) => axios.get('/lessons/csv', {
    params: _.pickBy({
      from: conditions.fromDate?.format('YYYYMMDD'),
      to: conditions.toDate?.format('YYYYMMDD'),
      positionCode: conditions.position?.code,
    }),
  }),

  /**
   * レッスンスケジュールCSVインポート
   * @param {File} csvFile レッスンスケジュールCSV
   */
  importLessonsCsv: (context, csvFile: File) => {
    const formData = new FormData();
    formData.append('csv', csvFile);
    return axios.post('/lessons/sync', formData);
  },

  /**
   * レッスン以外のスケジュールCSVダウンロード
   * @param {DownloadOtherSchedulesCsvConditions} conditions 条件
   */
  downloadOtherSchedulesCsv: (
    context,
    conditions: DownloadOtherSchedulesCsvConditions,
  ) => axios.get('/schedules/csv', {
    params: _.pickBy({
      from: conditions.fromDate?.format('YYYYMMDD'),
      to: conditions.toDate?.format('YYYYMMDD'),
      positionCode: conditions.position?.code,
      kind: conditions.scheduleType,
    }),
  }),

  /**
   * レッスン以外のスケジュールCSVインポート
   * @param {File} csvFile レッスン以外のスケジュールCSV
   */
  importSchedulesCsv: (context, csvFile: File) => {
    const formData = new FormData();
    formData.append('csv', csvFile);
    return axios.post('/schedules/sync', formData);
  },
};

export default actions;
